import React, { useState } from "react";
import NotebookExportDialog from "./NotebookExportDialog";

function NotebookHeader({ 
    headerMinimized,
    onBack,
    isEditingTitle,
    editingTitleValue,
    setEditingTitleValue,
    handleTitleKeyPress,
    handleTitleSave,
    handleTitleCancel,
    handleTitleEdit,
    notebook,
    currentPageIndex,
    pages,
    isSaving,
    saveError,
    toolPanelVisible,
    toggleToolPanel,
    sidebarVisible,
    toggleSidebar,
    performUndo,
    performRedo,
    canUndo,
    canRedo,
    clearCurrentPage,
    deleteCurrentPage,
    addNewPage,
    toggleHeader,
    fitContentToViewport,
    openDimensionModal,
    canvasSize,
    pageMode,
    canvasBackground,
    loadedPageIds
}) {
    // Export state stays out of the editor: the dialog owns its options and progress
    const [exportPages, setExportPages] = useState(null);

    const openExport = () => {
        // The pages the editor has loaded are exported as they are on screen
        const localPages = new Map();
        pages.forEach(page => {
            if (loadedPageIds?.has(page.id)) localPages.set(page.id, page);
        });
        setExportPages(localPages);
    };

    return (
        <div className={`floating-header modern ${headerMinimized ? 'minimized' : ''}`}>
            <div className="header-content-modern">
                {/* Left Section - Back & Title */}
                <div className="header-section-left">
                    <button 
                        className="back-btn-modern" 
                        onClick={onBack}
                        title="Back to notebooks"
                        aria-label="Back to notebooks"
                    >
                        <i className="fas fa-arrow-left"></i>
                    </button>
                    {!headerMinimized && (
                        <div className="notebook-info-modern">
                            {isEditingTitle ? (
                                <div className="title-editing-modern">
                                    <input
                                        type="text"
                                        value={editingTitleValue}
                                        onChange={(e) => setEditingTitleValue(e.target.value)}
                                        onKeyDown={handleTitleKeyPress}
                                        onBlur={handleTitleSave}
                                        className="title-input-modern"
                                        autoFocus
                                        maxLength={200}
                                        aria-label="Edit notebook title"
                                    />
                                    <div className="title-actions-modern">
                                        <button 
                                            className="title-action-btn save" 
                                            onClick={handleTitleSave}
                                            title="Save"
                                            aria-label="Save title"
                                        >
                                            <i className="fas fa-check"></i>
                                        </button>
                                        <button 
                                            className="title-action-btn cancel" 
                                            onClick={handleTitleCancel}
                                            title="Cancel"
                                            aria-label="Cancel editing"
                                        >
                                            <i className="fas fa-times"></i>
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <button 
                                    className="title-display-modern"
                                    onClick={handleTitleEdit} 
                                    title="Click to edit"
                                    aria-label="Edit notebook title"
                                >
                                    <h2>{notebook.title}</h2>
                                    <i className="fas fa-pen edit-icon"></i>
                                </button>
                            )}
                            <div className="status-bar-modern">
                                <span className="page-indicator-modern">
                                    Page {currentPageIndex + 1} of {pages.length}
                                </span>
                                
                                {/* Page Mode Badge */}
                                <span className={`mode-badge ${notebook?.page_mode || 'scaling'}`} 
                                      title={notebook?.page_mode === 'infinite' ? 'Infinite Canvas' : 'Scaling Canvas'}>
                                    {notebook?.page_mode === 'infinite' ? (
                                        <><i className="fas fa-infinity"></i> Infinite</>
                                    ) : (
                                        <><i className="fas fa-compress-arrows-alt"></i> Scaling</>
                                    )}
                                </span>
                                
                                {/* Page Dimensions for Infinite Pages */}
                                {notebook?.page_mode === 'infinite' && pages[currentPageIndex] && (
                                    <span className="dimensions-badge" title="Current page size">
                                        {Math.round(pages[currentPageIndex].width || 0)} × {Math.round(pages[currentPageIndex].height || 0)}
                                    </span>
                                )}
                                
                                {/* Save Status Indicator */}
                                {isSaving && (
                                    <span className="status-badge syncing">
                                        <i className="fas fa-sync-alt fa-spin"></i> Saving
                                    </span>
                                )}
                                {saveError && (
                                    <span className="status-badge failed" title={saveError}>
                                        <i className="fas fa-exclamation-circle"></i> Error
                                    </span>
                                )}
                            </div>
                        </div>
                    )}
                </div>
                
                {/* Right Section - Actions */}
                <div className="header-section-right">
                    {/* Panel Toggles */}
                    <div className="action-group">
                        <button 
                            className={`action-btn-modern ${!toolPanelVisible ? 'inactive' : 'active'}`}
                            onClick={toggleToolPanel}
                            title={toolPanelVisible ? "Hide tools" : "Show tools"}
                            aria-label={toolPanelVisible ? "Hide tools panel" : "Show tools panel"}
                        >
                            <i className="fas fa-palette"></i>
                        </button>
                        <button 
                            className={`action-btn-modern ${!sidebarVisible ? 'inactive' : 'active'}`}
                            onClick={toggleSidebar}
                            title={sidebarVisible ? "Hide pages" : "Show pages"}
                            aria-label={sidebarVisible ? "Hide pages panel" : "Show pages panel"}
                        >
                            <i className="fas fa-book"></i>
                        </button>
                    </div>
                    
                    {!headerMinimized && (
                        <>
                            {/* Page Actions */}
                            <div className="action-group">
                                <button 
                                    className="action-btn-modern"
                                    onClick={fitContentToViewport}
                                    title="Fit content to viewport"
                                    aria-label="Fit content to viewport"
                                >
                                    <i className="fas fa-compress-arrows-alt"></i>
                                </button>
                                
                                {pageMode === 'infinite' && (
                                    <button 
                                        className="action-btn-modern"
                                        onClick={openDimensionModal}
                                        title={`Canvas size: ${canvasSize?.width || 1200} × ${canvasSize?.height || 800}`}
                                        aria-label="Change canvas dimensions"
                                    >
                                        <i className="fas fa-crop-alt"></i>
                                    </button>
                                )}

                                <button 
                                    className="action-btn-modern"
                                    onClick={openExport}
                                    title="Export to PDF"
                                    aria-label="Export to PDF"
                                >
                                    <i className="fas fa-file-pdf"></i>
                                </button>
                            </div>
                            
                            {/* Destructive Actions */}
                            <div className="action-group">
                                <button 
                                    className="action-btn-modern danger"
                                    onClick={clearCurrentPage}
                                    title="Clear current page"
                                    aria-label="Clear current page"
                                >
                                    <i className="fas fa-eraser"></i>
                                </button>
                                <button 
                                    className="action-btn-modern danger"
                                    onClick={deleteCurrentPage}
                                    disabled={pages.length <= 1}
                                    title="Delete current page"
                                    aria-label="Delete current page"
                                >
                                    <i className="fas fa-trash-alt"></i>
                                </button>
                            </div>
                            
                            {/* Add Page */}
                            <div className="action-group">
                                <button 
                                    className="action-btn-modern add"
                                    onClick={addNewPage}
                                    title="Add new page"
                                    aria-label="Add new page"
                                >
                                    <i className="fas fa-plus"></i>
                                </button>
                            </div>
                        </>
                    )}
                    
                    {/* Minimize Toggle */}
                    <button 
                        className="action-btn-modern minimize NotebookEditor__hide-on-small-display"
                        onClick={toggleHeader}
                        title={headerMinimized ? "Expand" : "Minimize"}
                        aria-label={headerMinimized ? "Expand header" : "Minimize header"}
                    >
                        <i className={`fas ${headerMinimized ? 'fa-chevron-down' : 'fa-chevron-up'}`}></i>
                    </button>
                </div>
            </div>

            {exportPages && (
                <NotebookExportDialog
                    notebookId={notebook.id}
                    title={notebook.title}
                    pageCount={pages.length}
                    currentPage={currentPageIndex + 1}
                    background={canvasBackground}
                    localPages={exportPages}
                    onClose={() => setExportPages(null)}
                />
            )}
        </div>
    );
}

export default NotebookHeader;
